<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Container, Modal, Text } from '@Shelly/shelly-design-system';
	import { mobile } from 'src/services/mobile-rework';
	import { createEventDispatcher, onMount } from 'svelte';
	import { CHANGE_DATE } from 'src/constants';
	import { save_home } from 'src/helpers/home_helpers';
	import { dateFormat } from 'src/helpers/date_helper';
	import { push } from 'svelte-spa-router';
	import { get } from 'svelte/store';
	import { home_data, user_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
	import { capFirstChar } from 'src/helpers/utilities';
	import { open_external_link_android } from 'src/services/native_container_requests_android';
	import { open_external_link } from 'src/services/native_container_requests_ios';

	export let showGeneralTermsAndConditionsPopUpModal = false;
	export let showPrivacyPolicyPopUpModal = false;
	export let showDataActPopUpModal = false;
	export let priority = 90;

	const dispatch = createEventDispatcher();

	$: showModal =
		showGeneralTermsAndConditionsPopUpModal || showPrivacyPolicyPopUpModal || showDataActPopUpModal;

	const closeModal = (e: CustomEvent | MouseEvent) => {
		if (showModal) {
			e.preventDefault();

			showGeneralTermsAndConditionsPopUpModal &&
				save_home('gtc', {
					s: CHANGE_DATE.GTC,
				});
			showPrivacyPolicyPopUpModal &&
				save_home('pp', {
					s: CHANGE_DATE.PP,
				});

			showDataActPopUpModal &&
				save_home('dataAct', {
					s: CHANGE_DATE.DA,
					agreed: false,
					last_seen: Math.floor(Date.now() / 1000), //epoch time in seconds
				});

			dispatch('close');
		}
	};

	const handleContinue = () => {
		showGeneralTermsAndConditionsPopUpModal &&
			save_home('gtc', {
				s: CHANGE_DATE.GTC,
			});

		showPrivacyPolicyPopUpModal &&
			save_home('pp', {
				s: CHANGE_DATE.PP,
			});

		showDataActPopUpModal &&
			save_home('dataAct', {
				s: CHANGE_DATE.DA,
				agreed: true,
				last_seen: Math.floor(Date.now() / 1000), //epoch time in seconds
			});

		dispatch('close');
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
		if (showDataActPopUpModal) {
			save_home('dataAct', {
				s: CHANGE_DATE.DA,
				agreed: $home_data?.dataAct?.agreed || false,
				last_seen: Math.floor(Date.now() / 1000), //epoch time in seconds
			});
		}
	});

	setTimeout(() => {
		console.log('setting up support link listener');

		const links = [
			{
				target: document.getElementById('support-link'),
				callback: () => {
					if ($app) {
						if ($os === 'IOS') {
							open_external_link('mailto:support@shelly.cloud');
						} else {
							open_external_link_android('mailto:support@shelly.cloud');
						}
					} else {
						window.open('mailto:support@shelly.cloud', '_blank');
					}
				},
			},
			{
				target: document.getElementById('terms-link'),
				callback: () => {
					push('/terms-and-conditions');
				},
			},
			{
				target: document.getElementById('data-link'),
				callback: () => {
					push('/data-act');
				},
			},
			{
				target: document.getElementById('help-center-link'),
				callback: () => {
					if ($app) {
						if ($os === 'IOS') {
							open_external_link(
								`https://support.shelly.cloud/${$user_data.lang}/support/home`,
							);
						} else {
							open_external_link_android(
								`https://support.shelly.cloud/${$user_data.lang}/support/home`,
							);
						}
					} else {
						window.open(
							`https://support.shelly.cloud/${$user_data.lang}/support/home`,
							'_blank',
						);
					}
				},
			},
		];

		for (const link of links) {
			if (link.target) {
				link.target.addEventListener('click', link.callback);
			}
		}
	}, 300);

	let titleFormat = '';
	let documentType = '';
	let documentDate = '';

	if (showGeneralTermsAndConditionsPopUpModal && showPrivacyPolicyPopUpModal) {
		titleFormat = $format('title_modal_both_documents_updates', {
			default: 'Shelly Privacy Policy and General Terms and Conditions Updates',
		});
		documentType = `<span class="text-primary">${$format(
			'privacy',
		)}</span> ${$format('and')} <span class="text-primary">${$format('modal_terms_and_conditions_title')}</span>`;
		documentDate = `<span class="text-primary text-bold">${dateFormat(
			new Date(CHANGE_DATE.GTC * 1000),
			'd M, Y',
		)}</span>`;
	} else if (showGeneralTermsAndConditionsPopUpModal) {
		titleFormat = $format('title_modal_gtc_update', {
			default: 'Shelly General Terms and Conditions Updates',
		});
		documentType = `<span class="text-primary">${$format('modal_terms_and_conditions_title')}</span>`;
		documentDate = `<span class="text-primary text-bold">${dateFormat(
			new Date(CHANGE_DATE.GTC * 1000),
			'd M, Y',
		)}</span>`;
	} else if (showDataActPopUpModal) {
		titleFormat = $format('title_modal_data_act_update', {
			default: 'SHELLY DATA ACCESS AND USAGE',
		});
		documentType = `<span class="text-primary">${$format('data_act', { default: 'EU Data Act' })}</span>`;
		documentDate = `<span class="text-primary text-bold">${dateFormat(
			new Date(CHANGE_DATE.DA * 1000),
			'd M, Y',
		)}</span>`;
	} else {
		titleFormat = $format('title_modal_pp_update', {
			default: 'Shelly Privacy Policy Updates',
		});
		documentType = `<span class="text-primary">${$format('privacy')}</span>`;
		documentDate = `<span class="text-primary text-bold">${dateFormat(
			new Date(CHANGE_DATE.PP * 1000),
			'd M, Y',
		)}</span>`;
	}

	const supportLink = `<a id="support-link" class="link-styles">support@shelly.cloud</a>`;
	const termsAndConditions = `<a id="terms-link" class="link-styles" target="_blank">${$format(
		'modal_terms_and_conditions_title',
		{
			default: 'Terms and Conditions',
		},
	)}</a>`;
	const dataAct = `<a id="data-link" class="link-styles" target="_blank">${$format('data_act', {
		default: 'EU Data Act',
	})}</a>`;
	const helpCenterLink = `<a id="help-center-link" class="link-styles" target="_blank">${$format(
		'link_help_center',
		{
			default: 'Help Center',
		},
	)}</a>`;

	// Dynamic text decisions based on all modal conditions
	$: text1 = showDataActPopUpModal
		? $format('text_data_act_first_paragraph', {
				default:
					'In reference to the entering into force of Regulation (EU) 2023/2854 (EU Data Act) we introduce you with the required information about the data processed by the related service Shelly Smart Control.',
			})
		: $format('text_first_paragraph_document_updates', {
				default:
					'At Shelly, we are committed to protecting your privacy and ensuring the security of your personal information. To continue upholding these values and to comply with new regulations, we have made important updates to our {documentType}.',
				values: { documentType },
			});
	$: text2 = showDataActPopUpModal
		? $format('text_data_act_second_paragraph', {
				default:
					"In relation to the new regulation in order to continue providing you with the service and its functionalities, we have made some updates to Shelly App's General Terms and Conditions (GTC).",
			})
		: $format('text_second_paragraph_document_updates', {
				default:
					'The updated Privacy Policy will go into effect on {documentDate}. We encourage you to review the new policy to understand how these changes may impact you.',
				values: { documentDate },
			});
	$: text3 = showDataActPopUpModal
		? $format('text_data_act_third_paragraph', {
				default:
					'Please take a moment to review the updated documents and to understand how these may impact you:',
			})
		: $format('text_third_paragraph_document_updates', {
				default:
					'If you have any questions or concerns about these changes, please do not hesitate to reach out to our support team at {supportLink} or visit our {helpCenterLink}.',
				values: { supportLink, helpCenterLink },
			});
	$: text4 = $format('text_third_paragraph_document_updates', {
		default:
			'If you have any questions or concerns about these changes, please do not hesitate to reach out to our support team at {supportLink} or visit our {helpCenterLink}.',
		values: { supportLink, helpCenterLink },
	});
	$: text5 = $format('text_data_act_fifth_paragraph', {
		default: 'By clicking continue, you agree to our {termsAndConditions} and {dataAct}.',
		values: { termsAndConditions, dataAct },
	});

	function navigateToUrl(url: string): void {
		if (get(app)) {
			push(url);
			return;
		}

		window.open(`#${url}`, '_blank');
	}

	const isApp = $app;
</script>

<Modal {showModal} on:close={closeModal} title={titleFormat} backdropClose={false} zIndex={priority}>
	<div class="d-flex align-items-center flex-direction-column" slot="content">
		<img
			class="mb-l"
			src="./images/pp_gtc_modal_image.svg"
			alt="privacy-policy-and-general-terms-conditions"
		/>
		<!-- <Text class="mb-xl text-center" element="h1">{titleFormat}</Text> -->
		<Text class="mb-m" styleType={isApp ? 'body-extra-small' : 'body'}>{@html text1}</Text>
		<Text class="mb-m" styleType={isApp ? 'body-extra-small' : 'body'}>{@html text2}</Text>
		<Text class="mb-m" styleType={isApp ? 'body-extra-small' : 'body'}>{@html text3}</Text>
		{#if showDataActPopUpModal}
			<div class="text-center">
				<Text class="link-styles mb-m" on:click={() => navigateToUrl(`/terms-and-conditions`)}>
					{$format('button_view_updated_terms_conditions', {
						default: 'View updated Terms and Conditions',
					})}
				</Text>
				<Text
					class="link-styles mb-m"
					on:click={() => {
						navigateToUrl(`/data-act`);
					}}
				>
					{$format('button_view_data_act', {
						default: 'View Data Act Information',
					})}
				</Text>
				{#if showPrivacyPolicyPopUpModal}
					<Text class="link-styles mb-m" on:click={() => navigateToUrl(`/privacy-policy`)}>
						{$format('button_review_privacy_policy', {
							default: 'Review Privacy Policy',
						})}
					</Text>
				{/if}
			</div>
		{:else if (showPrivacyPolicyPopUpModal || showGeneralTermsAndConditionsPopUpModal) && !showDataActPopUpModal}
			<div class="mb-m text-center">
				{#if showPrivacyPolicyPopUpModal}
					<Text class="link-styles mb-m" on:click={() => navigateToUrl(`/privacy-policy`)}>
						{$format('button_review_privacy_policy', {
							default: 'Review Privacy Policy',
						})}
					</Text>
				{/if}
				{#if showGeneralTermsAndConditionsPopUpModal}
					<Text class="link-styles" on:click={() => navigateToUrl(`/terms-and-conditions`)}>
						{$format('button_review_general_terms_conditions', {
							default: 'Review General Terms and Conditions',
						})}
					</Text>
				{/if}
			</div>
		{/if}
		{#if showDataActPopUpModal}
			<Text class="mb-m" styleType={isApp ? 'body-extra-small' : 'body'}>{@html text4}</Text>
			<Text class="mb-m" styleType={isApp ? 'body-extra-small' : 'body'}>{@html text5}</Text>
		{/if}
	</div>

	<div class="d-flex justify-content-center align-items-center flex-direction-column" slot="footer">
		{#if showDataActPopUpModal}
			<Button class="mb-m" size="l" variant="primary" on:click={handleContinue}
				>{$format('button_continue', {
					default: 'Continue',
				})}</Button
			>
		{/if}

		{#if !showDataActPopUpModal}
			<!-- <Text class="link-styles" on:click={closeModal}
				>{capFirstChar(
					$format(`button_maybe_later`, { default: 'Maybe later' }),
				)}</Text
			> -->
			<Button class="mb-m" size="l" variant="secondary" on:click={closeModal}
				>{capFirstChar($format(`button_maybe_later`, { default: 'Maybe later' }))}</Button
			>
		{/if}
	</div>
</Modal>
